// Intent-matched ad-landing copy per category. Used to make each category page
// (and the /chat entry when arrived from a category) read like a purpose-built
// Google Ads landing page, plus category-level FAQ for rich results.
//
// Fields:
//   hero      — short offer/benefit line shown under the category H1
//   ctaHeading, ctaSub — the ConversionCTA band on the category page
//   chatH1, chatSub    — /chat entry hero when ?category=<slug> is present
//   faqs      — category-level Q&A (visible accordion + FAQPage JSON-LD)

const COPY = {
  retail: {
    hero: "Order stuck, refund late or a delivery gone missing? Reach the right store team fast.",
    ctaHeading: "Order or refund stuck? A specialist can help you sort it.",
    ctaSub:
      "Tell us the store and order number once and an independent specialist helps you reach the right official support and follow it through. Your first message is free.",
    chatH1: "Sort your order or refund.",
    chatSub:
      "Describe your retail issue — a late delivery, a missing refund, a wrong item — and connect with a specialist. Your first message is free.",
    faqs: [
      {
        q: "How do I find the official customer service number for a store?",
        a: "Every brand page on It's Genie Assist lists the official phone number and chat link, sourced from the retailer's own website with the date we last verified it. We're an independent directory and not affiliated with any store.",
      },
      {
        q: "What should I have ready before contacting a retailer?",
        a: "Have your order number, the email or phone on the account, and the item name and delivery date ready so support can find your order quickly.",
      },
      {
        q: "Can a specialist help me get a refund?",
        a: "A specialist can help you reach the right official support team and guide you through the refund process. We never take payment on the retailer's behalf and never ask for your card details.",
      },
    ],
  },
  crypto: {
    hero: "Locked out, waiting on a transfer or worried about your account? Get the right help safely.",
    ctaHeading: "Locked out of your crypto account? Talk to a specialist.",
    ctaSub:
      "Share your issue once and an independent specialist helps you reach the exchange's official support the safe way. We never ask for your seed phrase, password or codes. First message free.",
    chatH1: "Get back into your crypto account.",
    chatSub:
      "Describe your issue — a locked account, a pending transfer, a security worry — and connect with a specialist. We never ask for your seed phrase or passwords.",
    faqs: [
      {
        q: "Is it safe to get help with my crypto account here?",
        a: "Yes. It's Genie Assist is independent and we never ask for your seed phrase, private keys, password or one-time codes. Genuine exchange support will never ask for them either.",
      },
      {
        q: "What do I need before contacting a crypto exchange?",
        a: "Have the transaction ID (TxID) or wallet address, the email on your account, and a screenshot of the transaction ready.",
      },
      {
        q: "Can you recover my locked crypto account?",
        a: "We can't access your account, but a specialist can help you reach the exchange's official support and guide you through their verification and recovery steps.",
      },
    ],
  },
  social: {
    hero: "Hacked, locked out or account disabled? Reach real platform support faster.",
    ctaHeading: "Hacked or locked out of your account? A specialist can help.",
    ctaSub:
      "Tell us what happened once and an independent specialist helps you reach the platform's official recovery support. We never ask for your password. First message free.",
    chatH1: "Recover your social account.",
    chatSub:
      "Describe your issue — a hacked profile, a locked account, a disabled page — and connect with a specialist. We never ask for your password or codes.",
    faqs: [
      {
        q: "How do I recover a hacked social media account?",
        a: "Start on the platform's official account-recovery page. A specialist can help you reach the right support channel and walk you through identity verification. We never ask for your password.",
      },
      {
        q: "What should I have ready to recover my account?",
        a: "Have your username/handle and the email on file, screenshots of the problem, and any error message you saw.",
      },
      {
        q: "Are you affiliated with the social media platform?",
        a: "No. It's Genie Assist is an independent directory and not affiliated with any platform listed. We only point you to official, verified support channels.",
      },
    ],
  },
  email: {
    hero: "Can't sign in or lost access to your inbox? Get the right recovery steps fast.",
    ctaHeading: "Locked out of your inbox? A specialist can help you recover it.",
    ctaSub:
      "Share your issue once and an independent specialist helps you through the provider's official recovery process. We never ask for your password. First message free.",
    chatH1: "Get back into your inbox.",
    chatSub:
      "Describe your issue — a lost password, a locked account, missing emails — and connect with a specialist. We never ask for your password or codes.",
    faqs: [
      {
        q: "How do I recover access to my email account?",
        a: "Use your provider's official account-recovery page with your recovery email or phone. A specialist can help you find the right steps. We never ask for your password.",
      },
      {
        q: "What do I need to recover my email?",
        a: "Have your account email address, your recovery email or phone, and the device you normally sign in from ready.",
      },
      {
        q: "Does email support have a phone number?",
        a: "Most email providers offer online recovery rather than phone support. Each brand page shows the official channels available and the date we verified them.",
      },
    ],
  },
  streaming: {
    hero: "Can't play, billed twice or locked out? Fix your streaming account fast.",
    ctaHeading: "Streaming or billing issue? A specialist can help.",
    ctaSub:
      "Tell us what's wrong once and an independent specialist helps you reach the service's official support. Your first message is free.",
    chatH1: "Fix your streaming account.",
    chatSub:
      "Describe your issue — playback errors, a double charge, a locked account — and connect with a specialist. Your first message is free.",
    faqs: [
      {
        q: "How do I contact my streaming service about a billing charge?",
        a: "Each brand page lists the official phone number and chat link. Have the email on your subscription and the charge date ready before you reach out.",
      },
      {
        q: "What should I have ready for a streaming problem?",
        a: "Have the email on your subscription, your device and app version, and the title or episode that won't play.",
      },
      {
        q: "Can a specialist help with a refund for a wrong charge?",
        a: "A specialist can help you reach the service's official billing support and guide you through requesting a refund. We never charge on the service's behalf.",
      },
    ],
  },
  fintech: {
    hero: "Payment stuck, account frozen or an unauthorised charge? Get the right help safely.",
    ctaHeading: "Payment problem or frozen account? Talk to a specialist.",
    ctaSub:
      "Share your issue once and an independent specialist helps you reach the provider's official support safely. We never ask for your full card number or codes. First message free.",
    chatH1: "Sort your payment or account issue.",
    chatSub:
      "Describe your issue — a stuck transfer, a frozen account, an unknown charge — and connect with a specialist. We never ask for your full card number or codes.",
    faqs: [
      {
        q: "Is it safe to get help with my payment account here?",
        a: "Yes. It's Genie Assist is independent and we never ask for your full card number, password or one-time codes. Genuine support will never ask for them either.",
      },
      {
        q: "What should I have ready before contacting my bank or payment app?",
        a: "Have the last 4 digits of the card (never the full number), the exact charge amount and date, and the merchant name shown on your statement.",
      },
      {
        q: "Can you reverse a payment or unfreeze my account?",
        a: "We can't access your account, but a specialist can help you reach the provider's official support and guide you through their dispute or verification process.",
      },
    ],
  },
  travel: {
    hero: "Booking changed, refund pending or trip disrupted? Reach the right team fast.",
    ctaHeading: "Booking or refund trouble? A specialist can help.",
    ctaSub:
      "Tell us your trip details once and an independent specialist helps you reach the official support team. Your first message is free.",
    chatH1: "Sort your booking or refund.",
    chatSub:
      "Describe your issue — a cancelled booking, a pending refund, a trip change — and connect with a specialist. Your first message is free.",
    faqs: [
      {
        q: "How do I contact a travel company about my booking?",
        a: "Each brand page lists the official support number and chat link. Have your booking or confirmation number ready before you reach out.",
      },
      {
        q: "What do I need before contacting travel support?",
        a: "Have your booking / confirmation number, the travel dates and traveller names, and the email on the reservation ready.",
      },
      {
        q: "Can a specialist help me get a travel refund?",
        a: "A specialist can help you reach the company's official support and guide you through their refund or rebooking process.",
      },
    ],
  },
  tech: {
    hero: "Device won't work, a software error or an activation issue? Get the right support fast.",
    ctaHeading: "Device or software problem? A specialist can help.",
    ctaSub:
      "Describe your issue once and an independent specialist helps you reach the maker's official support. Your first message is free.",
    chatH1: "Fix your device or software.",
    chatSub:
      "Describe your issue — a device fault, an error message, an activation problem — and connect with a specialist. Your first message is free.",
    faqs: [
      {
        q: "How do I find official support for my device or software?",
        a: "Each brand page lists the maker's official support number and help centre, with the date we last verified it. We're independent and not affiliated with any maker.",
      },
      {
        q: "What should I have ready for a tech support call?",
        a: "Have your device model and OS version, the order or serial number, and a short description of the error ready.",
      },
      {
        q: "Can a specialist help me with a warranty or repair?",
        a: "A specialist can help you reach the maker's official support and guide you through their warranty, repair or activation process.",
      },
    ],
  },
  telecom: {
    hero: "Billing dispute, signal drop or a service outage? Reach your provider faster.",
    ctaHeading: "Billing or connection issue? A specialist can help.",
    ctaSub:
      "Tell us your provider and issue once and an independent specialist helps you reach official support. Your first message is free.",
    chatH1: "Sort your phone or internet issue.",
    chatSub:
      "Describe your issue — a billing dispute, a dropped signal, an outage — and connect with a specialist. Your first message is free.",
    faqs: [
      {
        q: "How do I reach my phone or internet provider's support?",
        a: "Each brand page lists the provider's official support number and chat link. Have your account or phone number ready before you reach out.",
      },
      {
        q: "What do I need before contacting my provider?",
        a: "Have your account or phone number, the name on the account, and the billing date in question ready.",
      },
      {
        q: "Can a specialist help with a billing dispute?",
        a: "A specialist can help you reach the provider's official billing support and guide you through disputing a charge or resolving an outage.",
      },
    ],
  },
};

export function getCategoryCopy(slug) {
  if (!slug) return null;
  return COPY[slug] || null;
}
